import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useApp, CURRENCIES } from '../context/AppContext';
import {
  FaChartPie, FaList, FaPlus, FaLightbulb,
  FaMoon, FaSun, FaBars, FaTimes, FaUserShield, FaEye
} from 'react-icons/fa';

const Navbar = () => {
  const location = useLocation();
  const { role, setRole, darkMode, setDarkMode, currency, currencyCode, setCurrencyCode } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { name: 'Dashboard', path: '/', icon: FaChartPie },
    { name: 'Transactions', path: '/transactions', icon: FaList },
    { name: 'Add Transaction', path: '/add-transaction', icon: FaPlus },
    { name: 'Insights', path: '/insights', icon: FaLightbulb },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav style={{
      background: darkMode
        ? 'rgba(8,12,20,0.75)'
        : 'rgba(255,255,255,0.65)',
      backdropFilter: 'blur(28px)',
      WebkitBackdropFilter: 'blur(28px)',
      borderBottom: darkMode
        ? '1px solid rgba(255,255,255,0.07)'
        : '1px solid rgba(148,163,184,0.2)',
      boxShadow: darkMode
        ? '0 4px 32px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.04) inset'
        : '0 4px 24px rgba(99,102,241,0.06), 0 1px 0 rgba(255,255,255,0.8) inset',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      transition: 'all 0.4s ease',
    }}>
      <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '66px' }}>

          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '12px',
              background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 50%, #a78bfa 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(99,102,241,0.5), 0 1px 0 rgba(255,255,255,0.2) inset',
              flexShrink: 0,
              transition: 'all 0.3s ease',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1) rotate(-5deg)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1) rotate(0deg)'; }}
            >
              <FaChartPie color="white" size={16} />
            </div>
            <div>
              <span style={{
                fontWeight: 900,
                fontSize: '1.15rem',
                letterSpacing: '-0.04em',
                background: darkMode
                  ? 'linear-gradient(135deg, #f1f5f9 0%, #818cf8 100%)'
                  : 'linear-gradient(135deg, #1e1b4b 0%, #6366f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>Finovo</span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }} className="hidden md:flex">
            {navItems.map(({ name, path, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.45rem',
                  padding: '0.5rem 0.9rem',
                  borderRadius: '11px',
                  fontSize: '0.85rem', fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                  background: isActive(path)
                    ? darkMode ? 'rgba(129,140,248,0.15)' : 'rgba(99,102,241,0.1)'
                    : 'transparent',
                  color: isActive(path)
                    ? 'var(--accent)'
                    : 'var(--text-secondary)',
                  boxShadow: isActive(path)
                    ? darkMode ? '0 0 16px rgba(129,140,248,0.15)' : '0 0 12px rgba(99,102,241,0.1)'
                    : 'none',
                  border: isActive(path)
                    ? darkMode ? '1px solid rgba(129,140,248,0.2)' : '1px solid rgba(99,102,241,0.15)'
                    : '1px solid transparent',
                }}
                onMouseEnter={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.06)';
                    e.currentTarget.style.color = 'var(--accent)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive(path)) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                <Icon size={13} />
                {name}
              </Link>
            ))}
          </div>

          {/* Right Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {/* Role Switcher */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(148,163,184,0.3)',
              borderRadius: '11px',
              padding: '0.4rem 0.7rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.3s ease',
            }}>
              {role === 'admin'
                ? <FaUserShield size={12} color="var(--accent)" />
                : <FaEye size={12} color="var(--text-muted)" />
              }
              <select
                id="role-switcher"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: role === 'admin' ? 'var(--accent)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  letterSpacing: '-0.01em',
                }}
              >
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            {/* Currency Picker */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(148,163,184,0.3)',
              borderRadius: '11px',
              padding: '0.4rem 0.7rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              transition: 'all 0.3s ease',
            }}>
              <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>{currency.flag}</span>
              <select
                id="currency-switcher"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  letterSpacing: '-0.01em',
                }}
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.symbol} {c.code}
                  </option>
                ))}
              </select>
            </div>

            {/* Dark Mode Toggle */}
            <button
              id="dark-mode-toggle"
              onClick={() => setDarkMode(!darkMode)}
              style={{
                width: '40px', height: '40px',
                borderRadius: '12px',
                background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.75)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(148,163,184,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                boxShadow: darkMode
                  ? '0 2px 12px rgba(0,0,0,0.3)'
                  : '0 2px 12px rgba(99,102,241,0.1)',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode
                ? <FaSun size={15} color="#fbbf24" style={{ filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.6))' }} />
                : <FaMoon size={15} color="#6366f1" />
              }
            </button>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                width: '40px', height: '40px', borderRadius: '12px',
                background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.75)',
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(148,163,184,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s',
                backdropFilter: 'blur(12px)',
                color: 'var(--text-primary)',
              }}
            >
              {mobileOpen ? <FaTimes size={15} /> : <FaBars size={15} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div style={{
            paddingBottom: '1rem',
            display: 'flex', flexDirection: 'column', gap: '0.3rem',
            borderTop: '1px solid var(--border)',
            paddingTop: '0.75rem',
            animation: 'fadeIn 0.2s ease',
          }} className="md:hidden">
            {navItems.map(({ name, path, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.7rem 1rem', borderRadius: '12px',
                  fontSize: '0.9rem', fontWeight: 600, textDecoration: 'none',
                  background: isActive(path) ? 'var(--accent-light)' : 'transparent',
                  color: isActive(path) ? 'var(--accent)' : 'var(--text-secondary)',
                  border: isActive(path) ? '1px solid var(--border-glow)' : '1px solid transparent',
                }}
              >
                <Icon size={15} />
                {name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
